import { Button, Space, Table, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import httpclient from "../../../service/httpclient";
import useTableColumnSearchProps from "../../../hooks/useTableColumnSearchProps";
import "./RecruitmentQuestionAllQuestionsTable.scss";
import { get } from "lodash";
import {
  BulbOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  CrownOutlined,
  EditOutlined,
  EyeOutlined,
  SyncOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import useWindowWidth from "../../../hooks/useWindowWidth";

const LATEST_RECRUITMENT_SEASON_LABEL = "2027秋招";

function getRandomNIndexes(arrLen, n) {
  const selectedIndexes = new Set();
  while (selectedIndexes.size < n) {
    const randomIndex = Math.floor(Math.random() * arrLen);
    selectedIndexes.add(randomIndex);
  }
  return Array.from(selectedIndexes);
}

function normalizeSource(source) {
  return source || "";
}

function getRecruitmentSeasonLabel(createdAt) {
  if (!createdAt) return null;

  const date = new Date(createdAt);
  if (date > new Date("2026-07-01")) return "2027秋招";
  if (date > new Date("2024-07-03")) return "2026秋招";

  return null;
}

function RecruitmentQuestionAllQuestionsTable() {
  const { deviceType } = useWindowWidth();
  const getSearchProps = useTableColumnSearchProps();
  const fixValuesFilterProps = (
    namePath,
    values,
    texts,
    cmp = (a, b, record) => a === b
  ) => ({
    filters: values.map((v, i) => ({ text: texts ? texts[i] : v, value: v })),
    onFilter: (value, record) => cmp(get(record, namePath), value, record),
  });

  const navigate = (id) => {
    window.open(
      `/recruitment-questions/${id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const [questionsData, setQuestionsData] = useState(null);
  const [randomNIndexes, setRandomNIndexes] = useState([]);
  const randomSize = 5;
  const fetchQuestionData = () =>
    httpclient.getAllVisibleRecruitmentQuestion().then((res) => {
      setRandomNIndexes(getRandomNIndexes(res.length, randomSize));
      setQuestionsData(res);
    });
  useEffect(() => {
    fetchQuestionData();
  }, []);
  // const sortedQuestionsData = questionsData && [
  //   ...randomNIndexes.map((i) => ({ random: true, ...questionsData[i] })),
  //   ...questionsData.filter((_, i) => !randomNIndexes.includes(i)),
  // ];
  const first5questionsWithVideo = questionsData
    ? questionsData.filter((q) => q.has_video_solution).slice(0, 5)
    : [];
  const rest = questionsData
    ? questionsData.filter((q) =>
        first5questionsWithVideo.every((v) => v.id !== q.id)
      )
    : [];
  const sortedQuestionsData = questionsData && [
    ...first5questionsWithVideo.map((q) => ({ random: true, ...q })),
    ...rest,
  ];
  const showSourceColumn = questionsData?.some((q) => q.show_source);
  const sourceFilters = Object.entries(
    (questionsData || []).reduce((sourceCounts, q) => {
      if (!q.from) return sourceCounts;
      const source = normalizeSource(q.from);
      sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      return sourceCounts;
    }, {})
  )
    .sort(
      ([sourceA, countA], [sourceB, countB]) =>
        countB - countA || sourceA.localeCompare(sourceB, "zh-Hans-CN")
    )
    .map(([source, count]) => ({ text: `${source}(${count})`, value: source }));

  const [allTags, setAllTags] = useState([]);
  useEffect(() => {
    httpclient.getAllKnowledgeTags().then((res) => setAllTags(res));
  }, []);

  const columns = [
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 60,
      render: (statusList) => (
        <Space>
          {statusList.includes("viewed") && <EyeOutlined />}
          {statusList.includes("answered") && <EditOutlined />}
          {statusList.includes("discussed") && <CommentOutlined />}
        </Space>
      ),
      ...fixValuesFilterProps(
        "status",
        ["discussed", /* "answered", */ "viewed", "null"],
        [
          <Space>
            <CommentOutlined />
            参与讨论
          </Space>,
          // <Space>
          //   <EditOutlined />
          //   有笔记
          // </Space>,
          <Space>
            <EyeOutlined />
            已浏览
          </Space>,
          "无",
        ],
        (statusList, status) =>
          status === "null"
            ? statusList.length === 0
            : statusList.includes(status)
      ),
    },
    // {
    //   title: "题号",
    //   dataIndex: "id",
    //   key: "id",
    //   width: 80,
    //   render: (id) => `${id}.`,
    //   ...getSearchProps("id"),
    // },
    {
      title: (
        <Space>
          题目
          {/* <Button
            shape="round"
            size="small"
            onClick={() =>
              setRandomNIndexes(
                getRandomNIndexes(questionsData.length, randomSize)
              )
            }
          >
            <SyncOutlined />
            换一批
          </Button> */}
        </Space>
      ),
      dataIndex: "title",
      key: "title",
      render: (title, record) => {
        const recruitmentSeasonLabel = getRecruitmentSeasonLabel(
          record.created_at
        );
        const recruitmentSeasonTagColor =
          recruitmentSeasonLabel === LATEST_RECRUITMENT_SEASON_LABEL
            ? "#1890ff"
            : "#8c8c8c";

        return (
          <div>
            <span>
              {record.id}. <strong>{title}</strong>
            </span>
            {recruitmentSeasonLabel && (
              <i
                style={{
                  fontSize: 10,
                  position: "absolute",
                  zIndex: 999,
                  right: 2,
                  top: 2,
                  color: recruitmentSeasonTagColor,
                  border: `${recruitmentSeasonTagColor} 1px solid`,
                  padding: "0 5px 0 3px",
                  borderRadius: 20,
                }}
              >
                {recruitmentSeasonLabel}
              </i>
            )}
          </div>
        );
      },
      width: 180,
      ellipsis: true,
      ...getSearchProps("title"),
    },
    {
      title: "解答",
      dataIndex: "solution",
      key: "solution",
      width: 80,
      render: (value, record) => (
        <Space>
          {!!value.answer && (
            <Tooltip title="有答案">
              <CheckCircleOutlined />
            </Tooltip>
          )}
          {!!value.analysis && (
            <Tooltip title="有提示">
              <BulbOutlined />
            </Tooltip>
          )}
          {!!record.has_video_solution && (
            <Tooltip title="有视频解答">
              <VideoCameraOutlined />
            </Tooltip>
          )}
        </Space>
      ),
      ...fixValuesFilterProps(
        "solution",
        ["answer", "analysis", "video", "null"],
        [
          <Space>
            <CheckCircleOutlined />
            答案
          </Space>,
          <Space>
            <BulbOutlined />
            提示
          </Space>,
          <Space>
            <VideoCameraOutlined />
            视频解答
          </Space>,
          "无",
        ],
        (solution, selected, record) => {
          if (selected === "null")
            return (
              !record.has_video_solution &&
              !solution.answer &&
              !solution.analysis
            );
          else if (selected === "video") return !!record.has_video_solution;
          else return !!solution[selected];
        }
      ),
    },
    ...(showSourceColumn
      ? [
          {
            title: (
              <Space size={4}>
                真题来源
                <Tooltip
                  title="题库会员专属权益"
                  trigger={deviceType === "mobile" ? "click" : "hover"}
                >
                  <CrownOutlined />
                </Tooltip>
              </Space>
            ),
            dataIndex: "from",
            key: "from",
            width: 120,
            ellipsis: true,
            render: normalizeSource,
            filters: sourceFilters,
            onFilter: (value, record) => normalizeSource(record.from) === value,
          },
        ]
      : []),
    {
      title: "难度",
      dataIndex: "difficulty",
      key: "difficulty",
      width: 80,
      ...fixValuesFilterProps("difficulty", ["简单", "中等", "困难"]),
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 80,
      ...fixValuesFilterProps("type", ["笔试", "面试"]),
    },
    {
      title: "题型",
      dataIndex: "format",
      key: "format",
      width: 80,
      ...fixValuesFilterProps("format", ["单选", "多选", "解答"]),
    },
    {
      title: "知识点标签",
      dataIndex: "tags",
      key: "tags",
      width: 240,
      render: (tags) => (
        <Space wrap size={[0, 2]}>
          {tags.map((tag) => (
            <Tag key={tag.id}>{tag.name}</Tag>
          ))}
        </Space>
      ),
      filters: allTags.map((tag) => ({ text: tag.name, value: tag.name })),
      onFilter: (value, record) =>
        record.tags.some((tag) => tag.name === value),
    },
  ];

  return (
    <div className="recruitment-question-all-question-table">
      <Table
        rowKey="id"
        rowClassName={(record) =>
          "question-row" + (record.random ? " random-selected" : "")
        }
        loading={sortedQuestionsData === null}
        onRow={(record) => ({ onClick: () => navigate(record.id) })}
        columns={columns}
        dataSource={sortedQuestionsData}
      />
    </div>
  );
}

export default RecruitmentQuestionAllQuestionsTable;
